import React from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedChip as Chip, LocalizedMenuItem as MenuItem, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { AccessTime, CheckCircle, WatchLater } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import PremiumTrialPopup from '../components/PremiumTrialPopup';
import { useAuth } from '../contexts/AuthContext';
import { usePremiumFeatureAccess } from '../hooks/usePremiumFeatureAccess';
import { hasPaidPlan } from '../utils/subscription';
import { translateInline } from '../i18n/inlineTranslations';
import { normalizeLanguage } from '../i18n/languages';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

const GOALS = [
  ['general_stability', 'General stability'],
  ['wealth', 'Wealth'],
  ['business', 'Business'],
  ['career_stability', 'Career stability'],
  ['leadership', 'Leadership'],
  ['public_marketing', 'Public image'],
  ['relationship_marriage', 'Relationship'],
  ['student', 'Study'],
  ['spiritual_growth', 'Spiritual growth'],
] as const;

const MOVEMENTS = ['quartz', 'automatic', 'mechanical', 'solar', 'digital', 'smartwatch'];
const DIAL_COLORS = ['white', 'cream', 'gold', 'rose_gold', 'silver', 'green', 'blue', 'black', 'red'];
const CASE_SHAPES = ['round', 'rounded_rectangle', 'square', 'rectangle', 'oval'];
const STRAPS = ['steel_chain', 'metal', 'gold_tone_metal', 'leather', 'rubber_silicone'];
const MARKERS = ['numbers', 'sticks', 'dots', 'none'];
const HAND_STYLES = ['normal', 'empty', 'arrow_tip', 'red'];
const HAND_COLORS = ['gold', 'black', 'blue', 'silver', 'peach', 'red', 'other'];
const SECOND_HAND_LENGTHS = ['reaches_markers', 'short', 'short_subdial', 'absent'];
const STRAP_DESIGNS = ['solid_links', 'open_links'];
const STRAP_LUGS = ['wide_robust', 'thin_fragile', 'unknown'];
const FITS = ['snug', 'loose', 'tight'];
const DIAL_DEPTHS = ['flat', 'deep_dish'];
const DIAL_ORIENTATIONS = ['outward', 'inward'];

const VALUE_HI: Record<string, string> = {
  general_stability: 'सामान्य स्थिरता',
  wealth: 'धन',
  business: 'बिजनेस',
  career_stability: 'करियर स्थिरता',
  leadership: 'लीडरशिप',
  public_marketing: 'पब्लिक इमेज',
  relationship_marriage: 'रिश्ता / विवाह',
  student: 'स्टडी',
  spiritual_growth: 'आध्यात्मिक विकास',
  budget: 'बजट',
  balanced: 'बैलेंस्ड',
  premium: 'प्रीमियम',
  quartz: 'क्वार्ट्ज',
  automatic: 'ऑटोमैटिक',
  mechanical: 'मैकेनिकल',
  solar: 'सोलर',
  digital: 'डिजिटल',
  smartwatch: 'स्मार्टवॉच',
  white: 'सफेद',
  cream: 'क्रीम',
  gold: 'गोल्ड',
  rose_gold: 'रोज गोल्ड',
  silver: 'सिल्वर',
  green: 'हरा',
  blue: 'नीला',
  black: 'काला',
  red: 'लाल',
  deep_red: 'गहरा लाल',
  dark_green: 'गहरा हरा',
  champagne: 'शैम्पेन',
  peach_green: 'पीच ग्रीन',
  light_green: 'हल्का हरा',
  round: 'राउंड',
  rounded_rectangle: 'राउंडेड रेक्टैंगल',
  square: 'स्क्वेयर',
  rectangle: 'रेक्टैंगल',
  oval: 'ओवल',
  steel_chain: 'स्टील चेन',
  metal: 'मेटल',
  gold_tone_metal: 'गोल्ड-टोन मेटल',
  leather: 'लेदर',
  rubber_silicone: 'रबर / सिलिकॉन',
  numbers: 'नंबर्स',
  sticks: 'स्टिक मार्कर्स',
  dots: 'डॉट्स',
  none: 'नंबर नहीं',
  any: 'कोई भी',
  left: 'बायां हाथ',
  right: 'दायां हाथ',
  yellow: 'पीला',
  off_white: 'ऑफ व्हाइट',
  normal: 'नॉर्मल',
  empty: 'खाली स्टाइल',
  arrow_tip: 'एरो टिप',
  other: 'अन्य',
  peach: 'पीच',
  reaches_markers: 'मार्कर्स तक पहुंचती है',
  short: 'छोटी',
  short_subdial: 'छोटा subdial second hand',
  absent: 'नहीं है',
  solid_links: 'solid links',
  open_links: 'open links/gaps',
  wide_robust: 'wide robust lugs',
  thin_fragile: 'thin fragile lugs',
  unknown: 'पता नहीं',
  snug: 'snug fit',
  loose: 'loose fit',
  tight: 'tight fit',
  flat: 'flat dial',
  deep_dish: 'deep-dish dial',
  outward: 'outward dial',
  inward: 'inward dial',
  risky: 'जोखिम वाला',
  excellent: 'बहुत अच्छा',
  good: 'अच्छा',
  fair: 'ठीक',
  weak: 'कमजोर',
};

const WEEKDAY_HI: Record<string, string> = {
  Sunday: 'रविवार',
  Monday: 'सोमवार',
  Tuesday: 'मंगलवार',
  Wednesday: 'बुधवार',
  Thursday: 'गुरुवार',
  Friday: 'शुक्रवार',
  Saturday: 'शनिवार',
};

const pretty = (value: any) => String(value || '-')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const prettyLabel = (value: any, isHindi: boolean) => {
  const key = String(value || '');
  if (isHindi && VALUE_HI[key]) return VALUE_HI[key];
  return pretty(value);
};

const joinPretty = (items: any[] = [], isHindi = false) => items.map((item) => prettyLabel(item, isHindi)).filter(Boolean).join(', ');

const cleanInternalText = (item: string) => String(item || '')
  .replace(/Day-2 logic adds execution rules:/gi, 'Recommendation guidance:')
  .replace(/Day-2 notes say/gi, 'The guidance says')
  .replace(/Day-2 notes warn/gi, 'The guidance warns')
  .replace(/Day-2 rule/gi, 'The guidance')
  .replace(/Day-2/gi, 'watch')
  .replace(/Day 2/gi, 'watch');

const translateModuleText = (item: string, isHindi: boolean) => {
  const cleaned = cleanInternalText(item);
  if (!isHindi || !item) return cleaned;
  const dictionary: Record<string, string> = {
    'The guide treats a watch as a small moving Vastu grid, so the result is based on movement, colour, dial numbers, shape, strap and wearing hand.': 'इस guide में watch को एक छोटे moving energy grid की तरह देखा गया है, इसलिए result movement, colour, dial numbers, shape, strap और wearing hand पर आधारित है।',
    'The recommendation is type-only: Kundlistar should not output any specific brand or model name.': 'यह recommendation केवल watch type के लिए है। Kundlistar कोई brand या model name suggest नहीं करता।',
    'Mulank colours are blended with goal-based colour guidance.': 'Mulank colours को आपके selected goals की colour guidance के साथ मिलाया गया है।',
    'Student/learning goals need a clean 3 and no digital distraction.': 'Student/learning goals के लिए 3 साफ दिखना और digital distraction कम रखना helpful माना गया है।',
    'Wealth, clients and relationship goals need 6 visible and cleanly enhanced.': 'Wealth, clients और relationship goals के लिए 6 number visible और cleanly enhanced होना helpful माना गया है।',
    'Confidence and leadership goals need 12 and 3 strong, with a clean executive shape.': 'Confidence और leadership goals के लिए 12 और 3 strong दिखें, साथ में clean executive shape हो।',
    'Choose an original, good-quality analogue watch; Kundlistar should not output brand/model names.': 'Original और अच्छी quality की analogue watch चुनें; Kundlistar brand/model names suggest नहीं करता।',
    'Quartz is acceptable for budget users; automatic is preferred for premium remedy use.': 'Budget users के लिए quartz ठीक है; premium remedy use में automatic ज्यादा preferred है।',
    'Avoid replicas, copied designs, fake stones and fake gold plating.': 'Replica, copied design, fake stones और fake gold plating avoid करें।',
    'Set the watch to exact local time; never keep it behind.': 'Watch को exact local time पर set रखें; इसे पीछे न रखें।',
    'At minimum, keep 12 and 6 clearly visible.': 'कम से कम 12 और 6 साफ visible रखें।',
    'For a stable general remedy, use 12, 3, 6 and 9 clearly, or all numbers if the dial remains clean.': 'Stable general guidance के लिए 12, 3, 6 और 9 साफ रखें; dial clean रहे तो सभी numbers भी ठीक हैं।',
    'Use solid stick markers only when they are not split and not cut by date/chronograph windows.': 'Solid stick markers तभी use करें जब वे split न हों और date/chronograph window से कट न रहे हों।',
    'For students, enhance 3 with a clean day/date/marker or subdial that does not cut the marker.': 'Students के लिए 3 को clean day/date/marker या subdial से enhance करें, लेकिन marker cut नहीं होना चाहिए।',
    'For relationship/marriage, keep 12 and 6 visible and enhance 6 cleanly.': 'Relationship/marriage के लिए 12 और 6 visible रखें और 6 को cleanly enhance करें।',
    'For wealth, support 6 and the 2/6/10 Artha points.': 'Wealth के लिए 6 और 2/6/10 Artha points को support करें।',
    '12 and 6 must be visible; 6 enhanced cleanly; 3 and 9 should stay clear': '12 और 6 visible होने चाहिए; 6 cleanly enhanced हो; 3 और 9 clear रहें',
    '12 and 6 visible minimum; 12, 3, 6 and 9 preferred': 'कम से कम 12 और 6 visible हों; 12, 3, 6 और 9 preferred हैं',
    '12, 3, 6 and 9 visible; 3 enhanced cleanly': '12, 3, 6 और 9 visible हों; 3 cleanly enhanced हो',
    '12, 3 and 6 visible; 12 and 3 enhanced cleanly': '12, 3 और 6 visible हों; 12 और 3 cleanly enhanced हों',
    '80% to 95% wrist-width coverage for balanced professional growth.': 'Balanced professional growth के लिए dial wrist-width का 80% से 95% रहे।',
    '80% to 95% for normal stability; 95% to 105% only for public-facing visibility roles.': 'Normal stability के लिए 80% से 95%; 95% से 105% केवल public-facing visibility roles में use करें।',
    'First wear on a suitable/lucky day if known, preferably during waxing moon; keep exact local time.': 'अगर suitable/lucky day पता हो तो उसी दिन first wear करें, preferably waxing moon में; exact local time रखें।',
    'digital/smartwatch as the main remedy watch': 'main remedy watch के रूप में digital/smartwatch',
    'stopped/dead watch or wrong time': 'रुकी हुई/dead watch या गलत time',
    'red or deep-red dial unless specifically prescribed': 'specific guidance के बिना red या deep-red dial',
    'dark-green dial unless specifically prescribed': 'specific guidance के बिना dark-green dial',
    'dots instead of clear numbers': 'clear numbers की जगह dots',
    'no-number dials for material/professional goals': 'material/professional goals के लिए no-number dial',
    'tiny numbers for growth or confidence goals': 'growth या confidence goals के लिए tiny numbers',
    'cut numbers or cut marker lines, especially 12/3/6/9': 'cut numbers या cut marker lines, खासकर 12/3/6/9',
    'overlapping chronographs or too many sub-dials': 'overlapping chronographs या बहुत ज्यादा sub-dials',
    'visible machine parts, skeleton/open-heart or tourbillon on the dial': 'dial पर visible machine parts, skeleton/open-heart या tourbillon',
    'busy images/artwork or faces on the dial': 'dial पर busy images/artwork या faces',
    'split-line hour markers': 'split-line hour markers',
    'inward-sloping dial': 'inward-sloping dial',
    'outer-bezel numbers replacing inner dial numbers': 'inner dial numbers की जगह outer-bezel numbers',
    'zero prefixes like 04, 06, 08 on the main dial': 'main dial पर 04, 06, 08 जैसे zero prefixes',
    'red arrows or lightning-bolt hands': 'red arrows या lightning-bolt hands',
    'downward triangles when anxious/angry': 'anxious/angry nature में downward triangles',
    'fake stones/materials, replicas or copied designs': 'fake stones/materials, replicas या copied designs',
    'gifted watch without token exchange': 'token exchange के बिना gifted watch',
    'broken glass/cracked dial': 'broken glass/cracked dial',
    'one busy dial trying to fix every life area': 'हर life area fix करने की कोशिश वाला busy dial',
    'Recommendation guidance: all three hands should be present, hands should be solid, the second hand should reach markers, the strap should be solid metal for primary daily wear, and the watch should fit snugly.': 'Recommendation guidance में execution rules जुड़ते हैं: तीनों hands होने चाहिए, hands solid हों, second hand markers तक पहुंचे, primary daily wear में solid metal strap हो और watch snug fit हो।',
    'Use all three hands: hour, minute and second. The second hand should be long enough to reach the markers.': 'तीनों hands use करें: hour, minute और second। Second hand markers तक पहुंचने जितना long होना चाहिए।',
    'Prefer simple solid hands without holes/skeleton cut-outs. Gold hands support prosperity/status; black or blue hands support focus and precision.': 'Holes/skeleton cut-outs के बिना simple solid hands prefer करें। Gold hands prosperity/status support करते हैं; black या blue hands focus और precision support करते हैं।',
    'Radium/lume is supportive when the hand body remains solid and the dial stays clean.': 'Hand body solid रहे और dial clean हो तो radium/lume supportive माना जाता है।',
    'Primary daily remedy watch should preferably use a solid metal strap with no open-link gaps. Leather is acceptable as a secondary watch if thick and robust.': 'Primary daily remedy watch में preferably solid metal strap हो, open-link gaps नहीं। Leather secondary watch में acceptable है अगर thick और robust हो।',
    'Wear snugly on the non-dominant hand; one-finger gap is fine, but the dial should not rotate or slide like a loose bracelet.': 'Non-dominant hand पर snug fit पहनें; one-finger gap ठीक है, लेकिन dial loose bracelet की तरह rotate या slide नहीं होना चाहिए।',
    'Aim around 12 mm for strong responsibility capacity; avoid very thin 6-10 mm cases for big-growth goals.': 'Strong responsibility capacity के लिए around 12 mm case thickness aim करें; big-growth goals में very thin 6-10 mm cases avoid करें।',
    'Prefer a flat readable dial; avoid deep-dish/depressed dials where the hands and goals look buried.': 'Flat readable dial prefer करें; deep-dish/depressed dial avoid करें जहां hands और goals buried लगते हैं।',
    'Prefer a new watch; if second-hand, service/clean it, set exact time and first wear from a lucky day.': 'New watch prefer करें; second-hand हो तो service/clean करें, exact time set करें और lucky day से first wear करें।',
    'For a gifted watch, receiver should pay a symbolic token amount such as ₹100 or ₹200 so the watch is treated as an energy exchange, not free time-transfer.': 'Gifted watch में receiver ₹100 या ₹200 जैसा symbolic token दे, ताकि watch energy exchange मानी जाए, free time-transfer नहीं।',
    'Keep all watches in your possession running; repair, remove battery, donate or retire dead watches.': 'अपने पास की सभी watches running रखें; dead watches को repair करें, battery निकालें, donate करें या retire करें।',
    'Do not sleep while wearing the watch; remove it at night for mental decompression.': 'Watch पहनकर न सोएं; रात में mental decompression के लिए इसे remove करें।',
    'all 1-12 numbers visible; 4, 5 and 6 must be clear, but not the only numbers shown': 'सभी 1-12 numbers visible हों; 4, 5 और 6 clear रहें, लेकिन सिर्फ यही तीन numbers न दिखें',
    'hollow/skeleton hands or holes in needles': 'hollow/skeleton hands या needles में holes',
    'missing second hand or very short subdial second hand': 'missing second hand या बहुत छोटा subdial second hand',
    'only 4/5/6 visible for influencers; use all numbers instead': 'influencers के लिए केवल 4/5/6 visible न हों; instead सभी numbers use करें',
    'unequal number sizes or misplaced 12/3/6/9 positions': 'unequal number sizes या misplaced 12/3/6/9 positions',
    'rubber/plastic strap as primary full-time remedy watch': 'primary full-time remedy watch में rubber/plastic strap',
    'open-link chain strap with large gaps': 'large gaps वाला open-link chain strap',
    'loose watch sliding or rotating on the wrist': 'wrist पर slide या rotate करती loose watch',
    'thin fragile case when taking big responsibility': 'big responsibility में thin fragile case',
    'deep-dish/depressed dial where goals look buried': 'deep-dish/depressed dial जहां goals buried लगते हैं',
    'wearing watch while sleeping': 'sleeping के समय watch पहनना',
    'dead/stopped watches kept in collection': 'collection में dead/stopped watches रखना',
  };
  if (dictionary[cleaned] || dictionary[item]) return cleanInternalText(dictionary[cleaned] || dictionary[item]);
  const firstWear = cleaned.match(/^First wear on ([A-Za-z]+) if practical, preferably during waxing moon; keep exact local time\.$/);
  if (firstWear) {
    return `Practical हो तो ${WEEKDAY_HI[firstWear[1]] || firstWear[1]} को first wear करें, preferably waxing moon में; exact local time रखें।`;
  }
  return cleaned
    .replace(/\bExcellent\b/g, 'बहुत अच्छा')
    .replace(/\bGood\b/g, 'अच्छा')
    .replace(/\bFair\b/g, 'ठीक')
    .replace(/\bWeak\b/g, 'कमजोर');
};

const translateGuidanceItem = translateModuleText;

const buildRecommendationLogic = (recommendation: any, isHindi: boolean) => {
  const type = recommendation.recommendedType || {};
  const enhancers = type.enhancerPositions || recommendation.preferredEnhancements || [];
  return [
    recommendation.mulank
      ? isHindi
        ? `Birth date से Mulank ${recommendation.mulank} आता है${recommendation.luckyDay ? `, इसलिए practical हो तो first wear के लिए ${recommendation.luckyDay} preferred है` : ''}.`
        : `Birth date gives Mulank ${recommendation.mulank}${recommendation.luckyDay ? `, so ${recommendation.luckyDay} is preferred for first wear when practical` : ''}.`
      : '',
    recommendation.recommendedColors?.length
      ? isHindi
        ? `Dial colours Mulank और goal logic से चुने गए हैं: ${joinPretty(recommendation.recommendedColors, true)}.`
        : `Dial colours are selected from Mulank and goal logic: ${joinPretty(recommendation.recommendedColors)}.`
      : '',
    recommendation.avoidColors?.length
      ? isHindi
        ? `इस profile के लिए avoid colours: ${joinPretty(recommendation.avoidColors, true)}.`
        : `Avoid colours for this profile: ${joinPretty(recommendation.avoidColors)}.`
      : '',
    type.movement?.length
      ? isHindi
        ? `Movement logic: ${joinPretty(type.movement, true)} preferred है, क्योंकि running और exact-time analog watch को discipline और timing symbol माना गया है.`
        : `Movement logic: ${joinPretty(type.movement)} is preferred because the module treats a running, exact-time analog watch as a discipline and timing symbol.`
      : '',
    type.caseShapes?.length
      ? isHindi
        ? `Shape logic: ${joinPretty(type.caseShapes, true)} dial को balanced और readable रखता है, cluttered या aggressive नहीं.`
        : `Shape logic: ${joinPretty(type.caseShapes)} keeps the dial balanced and readable instead of visually aggressive or cluttered.`
      : '',
    type.strapTypes?.length
      ? isHindi
        ? `Strap logic: ${joinPretty(type.strapTypes, true)} recommended colour और quality guidance के साथ match किया गया है.`
        : `Strap logic: ${joinPretty(type.strapTypes)} is matched with the recommended colour and quality guidance.`
      : '',
    type.handGuidance?.length
      ? isHindi ? `Hands logic: ${type.handGuidance.map((item: string) => translateModuleText(item, true)).join(' ')}` : `Hands logic: ${type.handGuidance.join(' ')}`
      : '',
    type.strapConstruction
      ? isHindi ? `Strap construction logic: ${translateModuleText(type.strapConstruction, true)}` : `Strap construction logic: ${type.strapConstruction}`
      : '',
    type.fitGuidance
      ? isHindi ? `Fit logic: ${translateModuleText(type.fitGuidance, true)}` : `Fit logic: ${type.fitGuidance}`
      : '',
    type.numberLayout
      ? isHindi ? `Number layout logic: ${translateModuleText(type.numberLayout, true)}.` : `Number layout logic: ${type.numberLayout}.`
      : '',
    recommendation.preferredMarkers?.length
      ? isHindi ? `Marker logic: ${recommendation.preferredMarkers.map((item: string) => translateModuleText(item, true)).join(' ')}` : `Marker logic: ${recommendation.preferredMarkers.join(' ')}`
      : '',
    enhancers.length
      ? isHindi
        ? `Enhancer logic: ${enhancers.map((item: any) => `${item.position} ${item.meaning} को support करता है`).join('; ')}.`
        : `Enhancer logic: ${enhancers.map((item: any) => `${item.position} supports ${item.meaning}`).join('; ')}.`
      : '',
    type.firstWearAdvice
      ? isHindi ? `First-wear logic: ${translateModuleText(type.firstWearAdvice, true)}` : `First-wear logic: ${type.firstWearAdvice}`
      : '',
  ].filter(Boolean);
};

const toggle = (items: string[], value: string) => (
  items.includes(value) ? items.filter((item) => item !== value) : [...items, value]
);

const todayDate = () => new Date().toISOString().slice(0, 10);

const WatchRecommendation: React.FC = () => {
  const { i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const isHindi = i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi');
  const text = React.useCallback((en: string, hi: string) => translateInline(language, en, hi), [language]);
  const { state: authState } = useAuth();
  const hasPaidAccess = hasPaidPlan(authState.user);
  const [birthDate, setBirthDate] = React.useState(todayDate());
  const [goals, setGoals] = React.useState<string[]>(['general_stability']);
  const [budgetMode, setBudgetMode] = React.useState<'budget' | 'balanced' | 'premium'>('balanced');
  const [recommendation, setRecommendation] = React.useState<any>(null);
  const [analysis, setAnalysis] = React.useState<any>(null);
  const [loading, setLoading] = React.useState<'recommend' | 'analyze' | null>(null);
  const { popup, loading: trialLoading, checkFeatureAccess, closePopup } = usePremiumFeatureAccess();
  const [watch, setWatch] = React.useState<any>({
    movement: 'quartz',
    dialColor: 'cream',
    caseShape: 'round',
    strapMaterial: 'metal',
    markerStyle: 'numbers',
    handsStyle: ['normal'],
    hasSecondHand: true,
    handColor: 'gold',
    secondHandLength: 'reaches_markers',
    strapDesign: 'solid_links',
    strapLugStrength: 'wide_robust',
    fit: 'snug',
    caseThicknessMm: 12,
    dialDepth: 'flat',
    dialOrientation: 'outward',
    isPrimaryDailyWear: true,
    gifted: false,
    tokenExchangePaid: true,
    wornWhileSleeping: false,
    deadWatchesInCollection: 0,
    isRunning: true,
    exactTimeSet: true,
    authenticQuality: true,
    visibleNumbers: [12, 3, 6, 9],
  });

  const profile = React.useMemo(() => ({ birthDate, goals }), [birthDate, goals]);
  const getAuthToken = () => authState.token || localStorage.getItem('nadiAstroToken') || '';

  const callPremiumEndpoint = async (
    endpoint: string,
    body: any,
    mode: 'recommend' | 'analyze',
    onSuccess: (data: any) => void
  ) => {
    const token = getAuthToken();
    if (!authState.isAuthenticated && !token && !authState.user) {
      window.alert(text('Please login to use Watch Recommendation.', 'Watch Recommendation use करने के लिए login करें।'));
      return;
    }
    if (!hasPaidAccess) {
      window.alert(text('Watch Recommendation is available to paid plan users only.', 'Watch Recommendation केवल paid plan users के लिए उपलब्ध है।'));
      return;
    }

    try {
      setLoading(mode);
      await checkFeatureAccess({
        featureKey: 'WATCH_RECOMMENDATION',
        apiCall: (trialAccessToken?: string) => axios.post(`${API_BASE_URL}${endpoint}`, { ...body, language }, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(trialAccessToken ? { 'x-premium-trial-token': trialAccessToken } : {}),
          },
        }),
        onSuccess,
      });
    } catch (error: any) {
      window.alert(error.response?.data?.message || error.message || text('Could not generate watch guidance.', 'Watch guidance generate नहीं हो सकी।'));
    } finally {
      setLoading(null);
    }
  };

  const getRecommendation = () => callPremiumEndpoint(
    '/wristwatch/recommend',
    { profile, options: { budgetMode } },
    'recommend',
    (data) => setRecommendation(data.data?.recommendation)
  );

  const analyzeWatch = () => callPremiumEndpoint(
    '/wristwatch/analyze',
    { profile, watch },
    'analyze',
    (data) => setAnalysis(data.data?.analysis)
  );

  return (
    <>
      <SEO
        title="Watch Recommendation by Astrology | Kundlistar"
        description="Get brand-free watch recommendation guidance based on birth date, goals, dial colour, movement, case shape and watch quality."
        path="/watch-recommendation"
        keywords="watch recommendation astrology, watch colour by birth date, Kundlistar watch guidance, astrology watch selection"
      />

      <Box sx={{ bgcolor: '#f7fbf8', minHeight: '100vh', py: { xs: 4, md: 7 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center" sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Chip icon={<WatchLater />} label={text('Premium Module', 'प्रीमियम मॉड्यूल')} color="primary" sx={{ mb: 2, fontWeight: 600 }} />
              <Typography variant="h2" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
                {text('Watch Recommendation', 'वॉच रिकमेंडेशन')}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                {text(
                  'Explore symbolic, brand-free watch guidance based on birth date, goals, dial colour, movement, shape, strap and exact time discipline. Astrology is used here as guidance, not as a guarantee or replacement for professional advice.',
                  'Birth date, goals, dial colour, movement, shape, strap और exact time discipline के आधार पर brand-free watch guidance देखें। Astrology को guidance की तरह use करें, guarantee या professional advice के replacement की तरह नहीं।'
                )}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
                {!authState.isAuthenticated && (
                  <Button component={RouterLink} to="/login" variant="contained" size="large">
                    {text('Login to Continue', 'जारी रखने के लिए login करें')}
                  </Button>
                )}
                <Button component={RouterLink} to="/watch-recommendation-app" variant="outlined" size="large">
                  {text('Learn About This Feature', 'इस feature के बारे में जानें')}
                </Button>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Card>
                <CardContent>
                  <Stack spacing={1.5}>
                    {[
                      text('Analog quartz or automatic watch guidance', 'Analog quartz या automatic watch guidance'),
                      text('Dial colours and case shape suggestions', 'डायल रंग और case shape suggestions'),
                      text('Current watch score and improvement tips', 'Watch score और सुधार tips'),
                      text('No brand or model recommendation', 'कोई brand या model recommendation नहीं')
                    ].map((item) => (
                      <Box key={item} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <CheckCircle color="primary" />
                        <Typography>{item}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {!hasPaidAccess && authState.isAuthenticated && (
            <Card sx={{ mb: 3, borderColor: '#f57c00' }}>
              <CardContent>
                <Typography fontWeight={600}>
                  {text('Watch Recommendation is available to paid plan users only.', 'Watch Recommendation केवल paid plan users के लिए उपलब्ध है।')}
                </Typography>
                <Button component={RouterLink} to="/upgrade" variant="contained" sx={{ mt: 2 }}>
                  {text('Choose a Paid Plan', 'Paid plan चुनें')}
                </Button>
              </CardContent>
            </Card>
          )}

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Card>
                <CardContent>
                  <Typography variant="h5" fontWeight={850} gutterBottom>
                    {text('Your Birth Date and Goals', 'आपकी birth date और goals')}
                  </Typography>
                  <TextField
                    label={text('Date of birth', 'जन्म तारीख')}
                    type="date"
                    value={birthDate}
                    onChange={(event) => setBirthDate(event.target.value)}
                    fullWidth
                    sx={{ my: 2 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Typography fontWeight={600} sx={{ mb: 1 }}>{text('Life focus', 'लाइफ फोकस')}</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {GOALS.map(([key, label]) => (
                      <Chip
                        key={key}
                        label={isHindi ? VALUE_HI[key] : label}
                        color={goals.includes(key) ? 'primary' : 'default'}
                        onClick={() => setGoals((prev) => toggle(prev, key))}
                      />
                    ))}
                  </Stack>
                  <TextField
                    select
                    label={text('Budget style', 'बजट स्टाइल')}
                    value={budgetMode}
                    onChange={(event) => setBudgetMode(event.target.value as any)}
                    fullWidth
                    sx={{ mt: 3 }}
                  >
                    {['budget', 'balanced', 'premium'].map((mode) => (
                      <MenuItem key={mode} value={mode}>{prettyLabel(mode, isHindi)}</MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{ mt: 3 }}
                    disabled={!hasPaidAccess || !!loading || trialLoading}
                    onClick={getRecommendation}
                  >
                    {loading === 'recommend' || trialLoading ? <CircularProgress size={24} color="inherit" /> : text('Get Watch Recommendation', 'Watch Recommendation पाएं')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 7 }}>
              {recommendation ? (
                <ResultCard title={text('Recommended Watch Type', 'सुझाया गया watch type')}>
                  <Grid container spacing={2}>
                    {[
                      [text('Movement', 'मूवमेंट'), joinPretty(recommendation.recommendedType?.movement || [], isHindi)],
                      [text('Dial colours', 'डायल रंग'), joinPretty(recommendation.recommendedType?.dialColors || [], isHindi)],
                      [text('Case shape', 'केस शेप'), joinPretty(recommendation.recommendedType?.caseShapes || [], isHindi)],
                      [text('Strap', 'स्ट्रैप'), joinPretty(recommendation.recommendedType?.strapTypes || [], isHindi)],
                      [text('Wearing hand', 'पहनने वाला हाथ'), prettyLabel(recommendation.recommendedType?.wearingHand || recommendation.preferredWrist || 'any', isHindi)],
                      [text('Dial size', 'डायल साइज'), translateModuleText(recommendation.recommendedType?.dialSize || recommendation.idealDialCoverage, isHindi)],
                      [text('Case thickness', 'Case thickness'), translateModuleText(recommendation.recommendedType?.caseThickness, isHindi)],
                      [text('Dial depth', 'Dial depth'), translateModuleText(recommendation.recommendedType?.dialDepth, isHindi)],
                      [text('Fit', 'Fit'), translateModuleText(recommendation.recommendedType?.fitGuidance, isHindi)],
                      [text('Strap construction', 'Strap construction'), translateModuleText(recommendation.recommendedType?.strapConstruction, isHindi)],
                    ].map(([label, value]) => (
                      <Grid key={label} size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ border: '1px solid #dce8df', borderRadius: 2, p: 2, height: '100%' }}>
                          <Typography variant="caption" color="text.secondary">{label}</Typography>
                          <Typography fontWeight={850}>{value || '-'}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  <GuidanceList title={text('Why this type', 'यह type क्यों')} items={(recommendation.whyThisType || []).map((item: string) => translateGuidanceItem(item, isHindi))} />
                  <GuidanceList title={text('Hands / Needles', 'Hands / needles')} items={(recommendation.recommendedType?.handGuidance || []).map((item: string) => translateGuidanceItem(item, isHindi))} />
                  <GuidanceList title={text('Recommendation', 'Recommendation')} items={buildRecommendationLogic(recommendation, isHindi)} />
                  <GuidanceList title={text('Avoid these features', 'ये features avoid करें')} items={(recommendation.avoidFeatures || []).map((item: string) => translateGuidanceItem(item, isHindi))} />
                  <GuidanceList title={text('Quality guidance', 'Quality guidance / quality सलाह')} items={(recommendation.qualityGuidance || []).map((item: string) => translateGuidanceItem(item, isHindi))} />
                </ResultCard>
              ) : (
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ minHeight: 280, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                    <AccessTime color="primary" sx={{ fontSize: 52, mb: 2 }} />
                    <Typography variant="h5" fontWeight={850}>{text('Your recommendation will appear here', 'आपकी recommendation यहां दिखेगी')}</Typography>
                    <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 520 }}>
                      {text('Select your focus and generate a brand-free watch type recommendation.', 'अपना focus चुनें और brand-free watch type recommendation generate करें।')}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h5" fontWeight={850} gutterBottom>
                {text('Check Current Watch', 'Current watch check करें')}
              </Typography>
              <Grid container spacing={2}>
                {[
                  ['movement', text('Movement', 'मूवमेंट'), MOVEMENTS],
                  ['dialColor', text('Dial colour', 'डायल रंग'), DIAL_COLORS],
                  ['caseShape', text('Case shape', 'केस शेप'), CASE_SHAPES],
                  ['strapMaterial', text('Strap', 'स्ट्रैप'), STRAPS],
                  ['markerStyle', text('Markers', 'मार्कर्स'), MARKERS],
                  ['handsStyle', text('Hand style', 'हैंड स्टाइल'), HAND_STYLES],
                  ['handColor', text('Hand colour', 'Hand colour'), HAND_COLORS],
                  ['secondHandLength', text('Second hand', 'Second hand'), SECOND_HAND_LENGTHS],
                  ['strapDesign', text('Strap design', 'Strap design'), STRAP_DESIGNS],
                  ['strapLugStrength', text('Lug strength', 'Lug strength'), STRAP_LUGS],
                  ['fit', text('Fit', 'Fit'), FITS],
                  ['dialDepth', text('Dial depth', 'Dial depth'), DIAL_DEPTHS],
                  ['dialOrientation', text('Dial orientation', 'Dial orientation'), DIAL_ORIENTATIONS],
                ].map(([key, label, options]) => (
                  <Grid key={key as string} size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      select
                      label={label as string}
                      value={Array.isArray(watch[key as string]) ? watch[key as string][0] : watch[key as string]}
                      fullWidth
                      onChange={(event) => setWatch((prev: any) => ({
                        ...prev,
                        [key as string]: key === 'handsStyle' ? [event.target.value] : event.target.value
                      }))}
                    >
                      {(options as string[]).map((option) => (
                        <MenuItem key={option} value={option}>{prettyLabel(option, isHindi)}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                ))}
              </Grid>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label={text('Case thickness mm', 'Case thickness mm')}
                    type="number"
                    value={watch.caseThicknessMm}
                    fullWidth
                    onChange={(event) => setWatch((prev: any) => ({ ...prev, caseThicknessMm: Number(event.target.value) }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label={text('Dead watches in collection', 'Dead watches collection में')}
                    type="number"
                    value={watch.deadWatchesInCollection}
                    fullWidth
                    onChange={(event) => setWatch((prev: any) => ({ ...prev, deadWatchesInCollection: Number(event.target.value) }))}
                  />
                </Grid>
              </Grid>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
                {[
                  ['isRunning', text('Running', 'चल रही है')],
                  ['exactTimeSet', text('Exact time set', 'सही time set')],
                  ['authenticQuality', text('Authentic quality', 'असली quality')],
                  ['hasSecondHand', text('Second hand present', 'Second hand है')],
                  ['isPrimaryDailyWear', text('Primary daily watch', 'Primary daily watch')],
                  ['gifted', text('Gifted watch', 'Gifted watch')],
                  ['tokenExchangePaid', text('Token paid', 'Token paid')],
                  ['wornWhileSleeping', text('Worn while sleeping', 'Sleeping में पहनते हैं')],
                ].map(([key, label]) => (
                  <Chip
                    key={key}
                    label={label}
                    color={watch[key] ? 'primary' : 'default'}
                    onClick={() => setWatch((prev: any) => ({ ...prev, [key]: !prev[key] }))}
                  />
                ))}
              </Stack>
              <Button
                variant="outlined"
                size="large"
                sx={{ mt: 3 }}
                disabled={!hasPaidAccess || !!loading || trialLoading}
                onClick={analyzeWatch}
              >
                {loading === 'analyze' ? <CircularProgress size={24} color="inherit" /> : text('Analyze Current Watch', 'Current watch analyze करें')}
              </Button>

              {analysis && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 3 }} />
                  <Typography variant="h5" fontWeight={850}>{text('Current Watch Score', 'मौजूदा watch score')}: {analysis.score}/100</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1 }}>{translateModuleText(analysis.summary, isHindi)}</Typography>
                  <GuidanceList
                    title={text('Fix first', 'पहले ये ठीक करें')}
                    items={(analysis.hits || [])
                      .filter((hit: any) => ['warning', 'critical'].includes(hit.severity))
                      .slice(0, 5)
                      .map((hit: any) => translateGuidanceItem(hit.recommendation || hit.description || hit.title, isHindi))}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>

      <PremiumTrialPopup
        open={popup.open}
        featureName={popup.featureName}
        used={popup.used}
        total={popup.total}
        remaining={popup.remaining}
        loading={trialLoading || !!loading}
        onCancel={closePopup}
        onContinue={popup.nextAction || (() => Promise.resolve())}
      />
    </>
  );
};

const ResultCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card>
    <CardContent>
      <Typography variant="h5" fontWeight={850} gutterBottom>{title}</Typography>
      {children}
    </CardContent>
  </Card>
);

const GuidanceList: React.FC<{ title: string; items: string[] }> = ({ title, items }) => {
  if (!items.length) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Typography fontWeight={850} sx={{ mb: 1 }}>{title}</Typography>
      <Stack spacing={1}>
        {items.map((item, index) => (
          <Box key={`${title}-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <CheckCircle color="primary" fontSize="small" sx={{ mt: 0.25 }} />
            <Typography color="text.secondary">{item}</Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default WatchRecommendation;
